// File: frontend/src/components/layout/DashboardFooter.js

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import styles from './DashboardFooter.module.css';

const LANGUAGES = {
  'en-us': { name: 'English (US)', flag: '🇺🇸' },
  'en-ca': { name: 'English (CA)', flag: '🇨🇦' },
  'es-mx': { name: 'Español (MX)', flag: '🇲🇽' },
  'fr-ca': { name: 'Français (CA)', flag: '🇨🇦' }
};

const DashboardFooter = () => {
  const currentYear = new Date().getFullYear();
  const { language, changeLanguage, t } = useLanguage();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setIsLanguageOpen(false);
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        {/* Language Picker */}
        <div className={styles.languagePicker} ref={dropdownRef}>
          <button
            className={styles.languageButton}
            onClick={() => setIsLanguageOpen(!isLanguageOpen)}
            aria-label={t('footer.language_selector_aria', 'Select language')}
          >
            <span className={styles.languageFlag}>
              {LANGUAGES[language]?.flag || '🌐'}
            </span>
            <span className={styles.languageName}>
              {LANGUAGES[language]?.name || t('footer.language_label', 'Language')}
            </span>
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none"
              className={`${styles.languageChevron} ${isLanguageOpen ? styles.languageChevronOpen : ''}`}
            >
              <path 
                d="M6 9L12 15L18 9" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isLanguageOpen && (
            <div className={styles.languageDropdown}>
              {Object.entries(LANGUAGES).map(([code, { name, flag }]) => (
                <button
                  key={code}
                  className={`${styles.languageOption} ${language === code ? styles.languageOptionActive : ''}`}
                  onClick={() => handleLanguageChange(code)}
                >
                  <span className={styles.languageFlag}>{flag}</span>
                  <span>{name}</span>
                  {language === code && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path 
                        d="M20 6L9 17L4 12" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Copyright */}
        <p className={styles.copyright}>
          {t('footer.copyright', '© {{year}} Allegory Technology Inc. All rights reserved.', { year: currentYear })}
        </p>
      </div>
    </footer>
  );
};

export default DashboardFooter;